import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiErrorDialog } from "../../../../hooks/utile/use-error-dialog";
import { useDidMount } from "../../../../hooks/utile/use-didmount";
import { useParamState } from "../../../../hooks/utile/use-param-state";
import { useValidation } from "../../../../hooks/utile/use-validation";
import { Api } from "../../../../module/api";
import { InputUtile } from "../../../../module/utile/input-rurules";
import { RoutingPath } from "../../../../routes/routing-path";
import RootStore from "../../../../store/root.store";
import { CustomButton } from "../../../ui/button/button";

const initialReissueState = {
  password: '',
  password_confirmation: '',
}

export const useReissue = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { dialog } = RootStore;
  const errDialog = useApiErrorDialog();
  const { validAll } = useValidation();
  const [verified, setVerified] = useState(false);
  const [load, setLoad] = useState(false);
  const [request, setRequest] = useParamState(initialReissueState, {
    password: InputUtile.eisu,
    password_confirmation: InputUtile.eisu,
  });

  const backToLogin = useCallback(() => {
    dialog.clear();
    navigate(RoutingPath.login);
  }, [dialog, navigate]);

  /* トークンが使用できない理由は判別しないため、一律で有効期限切れとして扱う */
  const pushExpiredMessage = useCallback(() => {
    dialog.pushMessage({
      messages: [
        'URLの有効期限が切れています。',
        'お手数ですが、再度お手続きをお願いいたします。',
      ],
      buttons: [
        { label: 'OK', callback: backToLogin },
      ],
    });
  }, [dialog, backToLogin]);

  useDidMount(async() => {
    if (!token) {
      pushExpiredMessage();
      return;
    }
    try {
      await Api.connect().verifyToken().post({ token });
      setVerified(true);
    } catch(e) {
      pushExpiredMessage();
    }
  });

  const validation = useMemo(() => {
    const {
      password,
      password_confirmation,
    } = request;
    return {
      password: validAll(password, 'require', 'length256', 'lengthOver8', 'password'),
      password_confirmation: validAll(password_confirmation, 'require', 'length256', 'lengthOver8', 'password'),
    }
  }, [request, validAll]);

  const isDisabled = useMemo(() => {
    return !!Object.values(validation).flat().length;
  }, [validation]);

  const onClickSubmit = useCallback(() => {
    /* 確認用との突合のみ、通信前にクライアントで判定する */
    if (request.password !== request.password_confirmation) {
      dialog.pushMessage({
        messages: ['確認用パスワードが一致しません。', '入力内容をご確認ください。'],
        buttons: [
          { label: 'OK', callback: () => dialog.pop() },
        ],
      });
      return;
    }
    setLoad(true);
    Api.connect().password().reset().put({ token, ...request })
    .then(() => {
      dialog.pushMessage({
        messages: ['パスワードの再発行が完了しました。'],
        buttons: [
          { label: 'OK', callback: backToLogin },
        ],
      })
    }).catch((e) => errDialog.push(e))
    .finally(() => setLoad(false));
  }, [token, request, dialog, errDialog, backToLogin]);

  const Footer = () => {
    return (
      <>
        <CustomButton
          colorType="secondary"
          onClick={backToLogin}
          isDisabled={load}
        >
          キャンセル
        </CustomButton>
        <CustomButton
          onClick={onClickSubmit}
          isDisabled={isDisabled || load}
        >
          OK
        </CustomButton>
      </>
    )
  }

  return {
    verified,
    Footer,
    request,
    setRequest,
    validation,
  }
}
